/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EEForm.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomFormTextFieldV2 from "../../common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import appTheme from "../../../../assets/AppTheme/appTheme";
import MultiselectConnectorV2 from "../../common/connectors/MultiselectConnectorV2";
import { useDispatch } from "react-redux";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import typeApprovalAPI from "../../../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../../../redux/actions/ruleEngineAction";
import customConsole from "../../../../config/customConsole";
import { Link } from "react-router-dom";
import SelectTextFieldN from "../../../text-fields/SelectTextFieldN";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import SelectVehicleComponentForVehicleSystem from "../../../../pages/labcar/SelectVehicleComponentForVehicleSystem";

function EEForm({
  title,
  getZoneEECountList,
  openEEComponentReplaceDialog,
  setOpenEEComponentReplaceDialog,
  handleEEComponentReplace,
  selectedEEComponentReplaceDetails,
  setSelectedEEComponentReplaceDetails,
  vsOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedZoneDetails,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  supplierOptionsList,
  disableFields = [],
}) {
  const [assemblyNo, setAssemblyNo] = useState("");
  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [supplierName, setSupplierName] = useState([]);
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVSIds, setSelectedVSIds] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);

  const [selectedASIL, setSelectedASIL] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetList, setRuleSetList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  const [ruleSetTree, setRuleSetTree] = useState(null);
  const [rulePath, setRulePath] = useState([]);

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath.length) return;

    const newPath = rulePath
      .slice(0, idx + 1)
      .map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else if (currentEntry?.node?.data?.category) {
      updateChangesAndClose();
      setRulePath(newPath);
      setShowRuleSetQuestionDialog(false);
      setRuleSetTree(null);
      setRulePath([]);
      setSelectedRuleSet("");
    } else {
      setRulePath(newPath);
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);
  const dispatch = useDispatch();
  const isDetailsView = disableFields?.includes("vehicleSystems");
  const normalizeArrayValue = (value) => {
    if (Array.isArray(value)) {
      return value.filter(
        (item) => item !== null && item !== undefined && item !== "",
      );
    }

    return value === null || value === undefined || value === "" ? [] : [value];
  };
  const normalizeKeywordIds = (keywords) =>
    (Array.isArray(keywords) ? keywords : [])
      .map((keyword) =>
        keyword && typeof keyword === "object"
          ? keyword?.id ?? keyword?.value
          : keyword,
      )
      .filter((id) => id !== null && id !== undefined && id !== "");
  const selectedSupplierOptions = normalizeArrayValue(
    selectedEEComponentReplaceDetails?.supplier_name,
  ).map((supplier) => ({ value: supplier, label: supplier }));
  const supplierOptionsForView = [
    ...(supplierOptionsList || []),
    ...selectedSupplierOptions.filter(
      (selectedSupplier) =>
        !(supplierOptionsList || []).some(
          (option) => String(option?.value) === String(selectedSupplier.value),
        ),
    ),
  ];
  const keywordOptionsForView = (
    selectedEEComponentReplaceDetails?.keywords || []
  ).map((keyword) => ({
    value: keyword?.id ?? keyword?.value ?? keyword,
    label: keyword?.name ?? keyword?.label ?? keyword,
  }));
  const vehicleSystemDetails =
    selectedEEComponentReplaceDetails?.vehicle_system_details || [];
  const vehicleSystemOptionsFromDetails = vehicleSystemDetails.map(
    (vehicleSystem, index) => ({
      value:
        vehicleSystem?.id ??
        selectedEEComponentReplaceDetails?.vehicle_systems?.[index] ??
        vehicleSystem?.name,
      label: vehicleSystem?.name || "-",
    }),
  );
  const selectedVehicleSystemIdsFromDetails =
    vehicleSystemOptionsFromDetails.map((vehicleSystem) => vehicleSystem.value);
  const vehicleComponentsFromDetails = [
    ...new Set(
      vehicleSystemDetails.flatMap((vehicleSystem) =>
        Array.isArray(vehicleSystem?.components)
          ? vehicleSystem.components.filter(Boolean)
          : [],
      ),
    ),
  ];

  useEffect(() => {
    if (
      Boolean(openEEComponentReplaceDialog) &&
      selectedEEComponentReplaceDetails
    ) {
      setAssemblyNo(selectedEEComponentReplaceDetails?.assembly_number || "");
      setSupplierName(
        normalizeArrayValue(selectedEEComponentReplaceDetails?.supplier_name),
      );
      setVersion(selectedEEComponentReplaceDetails?.version || "");
      setDescription(selectedEEComponentReplaceDetails?.description || "");
      setSummary(selectedEEComponentReplaceDetails?.summary || "");
      setSelectedKeywordIds(
        normalizeKeywordIds(selectedEEComponentReplaceDetails?.keywords),
      );
      setSelectedVSIds(
        (selectedEEComponentReplaceDetails?.vehicle_systems || [])
          ?.map(Number)
          ?.filter((vsId) => !Number.isNaN(vsId)),
      );
      setSelectedASIL(
        selectedEEComponentReplaceDetails?.other_fields?.asil_class || null,
      );
      setDiscp(selectedEEComponentReplaceDetails?.reason || "");
      setSelectedVariantsMany(
        variantOptionList
          ?.filter((variant) =>
            selectedEEComponentReplaceDetails?.variant_ids
              ?.map(String)
              ?.includes(String(variant?.value)),
          )
          ?.map((data) => String(data?.value)) || [],
      );
    }
  }, [openEEComponentReplaceDialog, selectedEEComponentReplaceDetails, variantOptionList]);

  const clearReplaceEEComponentFields = () => {
    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName([]);
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSelectedVSIds([]);
    setSelectedKeywordIds([]);
    setSelectedASIL(null);
    setDiscp("");
    setSelectedVariantsMany([]);
  };

  const handleCloseEEComponentReplace = () => {
    setOpenEEComponentReplaceDialog(false);
    clearReplaceEEComponentFields();
    setSelectedEEComponentReplaceDetails(null);
    setShowConfirmation(false);
    setShowIntersectionImpactPrompt(false);
  };

  const confirmUpdateAndRedirect = ({ willImptIntrOnly = false } = {}) => {
    if (selectedEEComponentReplaceDetails?.ee_id) {
      const reqObj = {
        current_time: new Date().toISOString(),
        variant_ids: selectedVariantsMany,
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo,
        ...(supplierName && { supplier_name: supplierName }),
        version: version,
        ...(summary && { summary }),
        description: description,
        vehicle_systems:
          selectedVSIds?.map((e) => (typeof e === "object" ? e.value : Number(e))) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: true,
        reason: discp,
      };

      return dispatch(
        HandleApiActions({
          ...labCar.replace_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      )
        .then((response) => {
          if (!response?.error) {
            handleCloseEEComponentReplace();
            willImptIntrOnly
              ? setActionType2DialogOpen(true)
              : setActionType1DialogOpen(true);
            if (willImptIntrOnly) {
              return dispatch(
                HandleApiActions({
                  ...labCar.update_action_type,
                  params: {
                    current_time: new Date().toISOString(),
                    project_id: Number(
                      selectedEEComponentReplaceDetails?.project_id,
                    ),
                    variant_id: Number(selectedSingleVariantDetails?.variant_id),
                    ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
                    is_dependency_linking: true,
                  },
                  show_toast: false,
                }),
              );
            }
          }
          return response;
        })
        .then(() => {
          getEEComponentsList();
          getSWModulesList();
        })
        .catch((error) => {
          console.error("Failed to replace EE component:", error);
        });
    }
    return Promise.resolve(null);
  };

  const handleCloseEEComponentReplaceDialog = () => {
    setOpenEEComponentReplaceDialog(false);
  };

  const checkForStandardWithRuleSet = () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      tickets_type: 2,
    };

    return dispatch(
      HandleApiActions({
        ...typeApprovalAPI.get_tickets,
        params: reqObj,
        show_toast: false,
      }),
    )
      .then((response) => {
        const standardsWithRuleSet = [
          ...new Set(
            Array.isArray(response?.data)
              ? response.data
                .map((ticket) => ticket?.ls_details?.standard_number)
                .filter(Boolean)
              : [],
          ),
        ];

        if (standardsWithRuleSet.length > 0) {
          return dispatch(
            HandleApiActions({
              ...ruleEngineActions.get_rule_engine_with_standards,
              params: { standard: standardsWithRuleSet },
              show_toast: false,
            }),
          ).then((standardWithRuleSet) => {
            if (
              standardWithRuleSet?.data &&
              (Array.isArray(standardWithRuleSet.data)
                ? standardWithRuleSet.data.length > 0
                : Object.keys(standardWithRuleSet.data).length > 0)
            ) {
              handleCloseEEComponentReplaceDialog();
              setShowRuleSetListDialog(true);
              setRuleSetList(standardWithRuleSet.data);
            } else {
              updateChangesWithoutStandardAndClose();
            }
            return standardWithRuleSet.data || [];
          });
        }
        updateChangesAndClose();
        return [];
      })
      .catch((error) => {
        console.error("Failed to check for standard with rule set:", error);
        return false;
      });
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseEEComponentReplace();
  };

  const viewRuleSetQuestions = (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    return dispatch(
      HandleApiActions({
        ...ruleEngineActions.get_rule_set_tree,
        params: {
          rule_set_id: ruleSetIdToUse,
        },
        show_toast: false,
      }),
    )
      .then((response) => {
        if (response?.data) {
          setRuleSetTree(response.data);
          const root = response.data?.tree
            ? { type: "question", data: response.data.tree }
            : null;
          if (root) {
            setShowRuleSetQuestionDialog(true);
            setRulePath([{ node: root, answer: null }]);
          }
        }
      })
      .catch((error) => {
        console.error("Failed to view rule set questions:", error);
      });
  };

  const updateChangesWithoutStandardAndClose = () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany,
      project_id: Number(selectedEEComponentReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
      version: version,
      will_it_impact: false,
      requires_homologation: true,
      reason: discp,
    };

    return dispatch(
      HandleApiActions({
        ...labCar.update_ee_component,
        params: reqObj,
        show_toast: true,
      }),
    )
      .then((response) => {
        getEEComponentsList();
        getSWModulesList();
        if (!response?.error) {
          handleCloseEEComponentReplace();
        }
        if (response?.data?.snow_resp) {
          setTicketsDialogOpen(true);
        }
      })
      .catch((error) => {
        console.error("Failed to update changes without standard:", error);
      });
  };

  const updateChangesAndClose = () => {
    const lastNode = rulePath.at(-1)?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;
    if (selectedEEComponentReplaceDetails?.ee_id) {
      const reqObj = {
        variant_ids: selectedVariantsMany,
        current_time: new Date().toISOString(),
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo,
        supplier_name: supplierName,
        version: version,
        ...(summary && { summary }),
        description: description,
        vehicle_systems: selectedVSIds?.map(Number) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
      };
      return dispatch(
        HandleApiActions({
          ...labCar.replace_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      )
        .then((response) => {
          getEEComponentsList();
          getSWModulesList();
          if (!response?.error) {
            handleCloseEEComponentReplace();
          }
          if (response?.data?.snow_resp) {
            setTicketsDialogOpen(true);
          }
        })
        .catch((error) => {
          console.error("Failed to update changes and close:", error);
        });
    }
    return Promise.resolve(null);
  };

  useEffect(() => {
    customConsole({ selectedVSIds, vsOptionsList });
  }, [selectedVSIds, vsOptionsList]);

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  return (
    <Box>
      <Stack
        spacing={2}
        mb={2}
        justifyContent={"center"}
        sx={{ width: "100%" }}
      >
        <CustomFormTextFieldV2
          size={"small"}
          label={"Assembly No"}
          placeholder={"Assembly No*"}
          type={"text"}
          fldType={"textR"}
          value={assemblyNo}
          setValue={setAssemblyNo}
          valueError={assemblyNoError}
          setValueError={setAssemblyNoError}
          disabled={disableFields.includes("assemblyNo")}

        />

        {disableFields.includes("supplierName") ? (
          <MultiselectConnectorV2
            multiple
            selectedValues={supplierName || []}
            setSelectedValues={() => {}}
            label={"Select Supplier Name"}
            optionsList={supplierOptionsForView}
            placeholder={false}
            disabled
          />
        ) : (
          <CustomSelectV2
            size={"small"}
            required={false}
            label={"Select Supplier Name"}
            optionsList={supplierOptionsList}
            selectedValue={supplierName?.[0] || ""}
            handleOnChange={(e) =>
              setSupplierName(normalizeArrayValue(e.target.value))
            }
            disabled={disableFields.includes("supplierName")}
          />
        )}
        <CustomFormTextFieldV2
          size={"small"}
          label={"Version*"}
          placeholder={"Version"}
          type={"text"}
          fldType={"textR"}
          value={version}
          setValue={setVersion}
          valueError={versionError}
          setValueError={setVersionError}
          disabled={disableFields.includes("version")}
        />
        <CustomFormTextFieldV2
          size={"small"}
          label={"Description"}
          placeholder={"Description*"}
          type={"text"}
          fldType={"text"}
          value={description}
          setValue={setDescription}
          valueError={descriptionError}
          setValueError={setDescriptionError}
          disabled={disableFields.includes("description")}
        />
        <CustomFormTextFieldV2
          size={"small"}
          label={"Summary"}
          placeholder={"Summary"}
          type={"text"}
          fldType={"text"}
          value={summary}
          setValue={setSummary}
          valueError={summaryError}
          setValueError={setSummaryError}
          disabled={disableFields.includes("summary")}
        />

        {isDetailsView ? (
          <>
            <MultiselectConnectorV2
              multiple
              label={"Select Vehicle Systems"}
              optionsList={vehicleSystemOptionsFromDetails}
              selectedValues={selectedVehicleSystemIdsFromDetails}
              setSelectedValues={() => {}}
              disabled
              placeholder={false}
            />
            <CustomFormTextFieldV2
              size={"small"}
              label={"Select Vehicle Components"}
              placeholder={"Select Vehicle Components"}
              type={"text"}
              fldType={"textO"}
              value={vehicleComponentsFromDetails.join(", ")}
              setValue={() => {}}
              disabled
            />
          </>
        ) : (
          <SelectVehicleComponentForVehicleSystem
            open={openEEComponentReplaceDialog}
            projectId={selectedProjectDetails?.project_id}
            variantId={selectedSingleVariantDetails?.variant_id}
            isZonal={selectedProjectDetails?.is_zonal}
            zonePosition={selectedZoneDetails?.position}
            setVcIdError={() => {}}
            vcId={
              selectedEEComponentReplaceDetails?.vehicle_component_details?.[0]
                ?.vc_id
            }
            vsId={
              selectedEEComponentReplaceDetails?.vehicle_component_details?.[0]
                ?.vs_id ??
              selectedEEComponentReplaceDetails?.vehicle_systems?.[0]
            }
          />
        )}


        <CustomSelectV2
          required={false}
          size={"small"}
          label={"Select ASIL Class"}
          optionsList={["A", "B", "C", "D"]?.map((el) => ({
            value: el,
            label: el,
          }))}
          selectedValue={selectedASIL}
          handleOnChange={(e) => setSelectedASIL(e.target.value)}
          disabled={disableFields.includes("asilClass")}
        />

        <MultiselectConnectorV2
          multiple
          selectedValues={selectedKeywordIds || []}
          setSelectedValues={setSelectedKeywordIds}
          disabled={disableFields.includes("keywords")}
          label={"Keywords"}
          optionsList={keywordOptionsForView}
          placeholder={false}
        />

        <MultiselectConnectorV2
          multiple
          selectedValues={selectedVariantsMany}
          setSelectedValues={setSelectedVariantsMany}
          disabled={disableFields.includes("variants")}
          label={"Select Variants"}
          optionsList={(variantOptionList || [])?.filter((variants) => {
            const currentId = selectedSingleVariantDetails?.variant_id;
            const alreadyAdded = (
              selectedEEComponentReplaceDetails?.variant_ids || []
            )
              ?.map(String)
              ?.includes(String(variants?.value));
            return (
              alreadyAdded || String(variants?.value) === String(currentId)
            );
          })}
          immutableValues={(() => {
            const opt = (variantOptionList || [])?.find(
              (v) =>
                String(v?.value) ===
                String(selectedSingleVariantDetails?.variant_id),
            );
            return opt?.value == null ? [] : [opt.value];
          })()}
          placeholder={false}
        />


        {showConfirmation && (
          <Stack spacing={2}>
            <Typography variant="body1">
              Will it impact the existing dependencies?
            </Typography>
            <Stack direction="row" spacing={2} justifyContent={"center"}>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={confirmUpdateAndRedirect}
              >
                Yes
              </Button>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={() => {
                  setShowConfirmation(false);
                  setShowIntersectionImpactPrompt(true);
                }}
              >
                No
              </Button>
            </Stack>
          </Stack>
        )}

        {showIntersectionImpactPrompt && (
          <Stack spacing={2}>
            <Typography variant="body1">
              Will it impact the existing intersection?
            </Typography>
            <Stack direction="row" spacing={2} justifyContent={"center"}>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={() =>
                  confirmUpdateAndRedirect({ willImptIntrOnly: true })
                }
              >
                Yes
              </Button>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={checkForStandardWithRuleSet}
              >
                No
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>


      <Box>
        <Dialog
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          slotProps={{ backdrop: { style: { backgroundColor: "transparent" } } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          <IconButton
            onClick={handleActionType1DialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                ECU dependency linking is pending!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/labcar?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`}
                >
                  <Button
                    sx={{ ...appTheme.commonBtnStyle }}
                    onClick={() => setIsLinkSoftwareModulesScreen(true)}
                  >
                    Link Now
                  </Button>
                </Link>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleActionType1DialogClose}
                >
                  Link Later
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

        <Dialog
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          slotProps={{ backdrop: { style: { backgroundColor: "transparent" } } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          <IconButton
            onClick={handleActionType2DialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                ECU intersection linking is pending!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/type-approval?project_id=${selectedProjectDetails?.project_id}&variant_id=${selectedSingleVariantDetails?.variant_id}&ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`}
                >
                  <Button
                    sx={{ ...appTheme.commonBtnStyle }}
                    onClick={handleActionType2DialogClose}
                  >
                    Link Now
                  </Button>
                </Link>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleActionType2DialogClose}
                >
                  Link Later
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

        <Dialog
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          slotProps={{ backdrop: { style: { backgroundColor: "transparent" } } }}
        >
          <DialogTitle align="left">Tickets Recreated</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                Successfully created the tickets for the intersections!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/type-approval?project_id=${selectedProjectDetails?.project_id
                    }&setTabValue=${2}&variant_id=${selectedSingleVariantDetails?.variant_id
                    }`}
                >
                  <Button sx={{ ...appTheme.commonBtnStyle }}>View</Button>
                </Link>

                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleTicketsDialogClose}
                >
                  Close
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
        <Dialog
          open={showRuleSetListDialog}
          onClose={handleRuleSetListDialogClose}
        >
          <DialogTitle align="left">Select Rule Set</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ minWidth: 320 }}>
              <SelectTextFieldN
                size={"small"}
                label={"Select Rule Set"}
                variant={"standard"}
                optionsList={
                  ruleSetList?.map((rs) => ({
                    value: rs?.rule_set_id || rs?.id,
                    label: rs?.name,
                  })) || []
                }
                selectedValue={selectedRuleSet}
                handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
                sltFldStyle={{ width: "100%", marginTop: 6 }}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleRuleSetListDialogClose}
                >
                  Cancel
                </Button>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={async () => {
                    await viewRuleSetQuestions(selectedRuleSet);
                    setShowRuleSetListDialog(false);
                  }}
                  disabled={!selectedRuleSet}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
        >
          <DialogTitle>Rule Set: {ruleSetTree?.name || ""}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ minWidth: 360 }}>
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  const keyId = entry.node?.data?.id || entry.node?.data?.text || `step-${idx}`;
                  return (
                    <Box
                      key={keyId}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {isCategory ? (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>

                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              size="small"
                              variant={
                                entry.answer === "yes"
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() => handleAnswerAt(idx, "yes")}
                              sx={{
                                ...appTheme.commonBtnStyle,
                                mr: 1,
                                ...(entry.answer === "no" && {
                                  color: "#888",
                                  borderColor: "#d0d0d0",
                                  backgroundColor: "#f5f5f5",
                                  "&:hover": {
                                    backgroundColor: "#eeeeee",
                                    borderColor: "#c0c0c0",
                                  },
                                }),
                              }}
                            >
                              Yes
                            </Button>
                            <Button
                              size="small"
                              variant={
                                entry.answer === "no" ? "contained" : "outlined"
                              }
                              onClick={() => handleAnswerAt(idx, "no")}
                              sx={{
                                ...appTheme.commonBtnStyle,
                                ...(entry.answer === "yes" && {
                                  color: "#888",
                                  borderColor: "#d0d0d0",
                                  backgroundColor: "#f5f5f5",
                                  "&:hover": {
                                    backgroundColor: "#eeeeee",
                                    borderColor: "#c0c0c0",
                                  },
                                }),
                              }}
                            >
                              No
                            </Button>
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {rulePath?.length > 0 &&
                  rulePath.at(-1)?.node?.type === "category" ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    mt={2}
                  >
                    <Button
                      sx={{ ...appTheme.commonBtnStyle }}
                      onClick={() => {
                        updateChangesAndClose();
                        setShowRuleSetQuestionDialog(false);
                        setRuleSetTree(null);
                        setRulePath([]);
                        setSelectedRuleSet("");
                        handleCloseEEComponentReplace();
                      }}
                    >
                      Finish
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
}

EEForm.propTypes = {
  title: PropTypes.string,

  openEEComponentReplaceDialog: PropTypes.bool,
  setOpenEEComponentReplaceDialog: PropTypes.func,

  getZoneEECountList: PropTypes.func,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,

  handleEEComponentReplace: PropTypes.func,
  setSelectedEEComponentReplaceDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,

  vsOptionsList: PropTypes.array,
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,

  selectedEEComponentReplaceDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedZoneDetails: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.object,

  disableFields: PropTypes.array,
};

export default EEForm;

const styles = {
  closeButton: {
    color: "black",
    position: "absolute",
    top: { xs: "2px", sm: "4px" },
    right: { xs: "4px", sm: "8px" },
  },
  eeCompMainContainer: {
    flex: 1,
    minWidth: 0,
    minHeight: { xs: "350px", sm: "400px", md: "500px" },
    maxHeight: { xs: "500px", sm: "600px", md: "700px" },
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "stretch",
    boxSizing: "border-box",
  },
  eeCompCard: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  eeCompCardHeader: {
    py: { xs: 0.3, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
  },
  eeCompCardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompCardActions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid lightgrey",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompsList: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  eeCompsListItemContainer: {
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    gap: 0.5,
  },
  noDataAvailable: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  txtStyle: {
    width: "270px",
  },
  txtStyleR: {
    width: "270px",
  },
  edRpIconsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 0.5, sm: 1 },
  },
  customTextFieldN: {
    width: "270px",
  },
  selectTxtStyle: {
    width: "270px",
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
};
